/* eslint-disable eqeqeq */
/* eslint-disable no-param-reassign */
import { Audio, Images, Tweens } from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import gameData from '../gameData';
import { ADDITION, MULTIPLICATION } from '../gameParts';
import NumberTile from './NumberTile';

export default class PlayingGrid extends Phaser.GameObjects.Container {
  static get TILES_ERASED() {return 'TILES_ERASED';};

  static get TARGET_HIT() {return 'TARGET_HIT';};

  static get STAR_COLLECTED() {return 'STAR_COLLECTED';};

  static get LEVEL_COMPLETED() {return 'LEVEL_COMPLETED';};

  static get GRID_CREATED() {return 'GRID_CREATED';};

  constructor(scene) {
    super(scene);

    this.totalRows = 5;
    this.totalCols = 5;

    this.positions = [];
    this.tiles = [];
    this.tilesSelected = [];
    this.emptySpaces = [];

    this.currentLevel = 1;
    this.maxTileValue = 0;

    this.scheduledVerticalMoves = {};
    this.scheduledHorizontalMoves = {};
    this.starsCollected = 0;
    this.star = null;
    this.starIsShowing = false;
    this.starsShown = 0;
    this.yellowStar1 = null;
    this.yellowStar2 = null;
    this.yellowStar3 = null;
    this.enabled = true;

    // recipes for patterns are as follows.  the first block is selected and the
    // pattern to find the next 2 blocks is given. If a tile is n the pattern is null
    // or if pattern goes off the grid, we test for the next pattern to get a
    // valid target number
    if (gameData.gameType === ADDITION) {
      this.patterns = [
        [[1,0], [1,0]],  // straight horiz
        [[0,1], [0,1]],  // straight vert
        [[0,1], [1,0]],  // L shape
        [[0,1], [-1,0]],  // backwards L shape
        [[0,-1], [1,0]],  // upside down L
        [[0,-1], [-1,0]],  // upside down backwards L shape
      ];
    }
    else if (gameData.gameType === MULTIPLICATION) {
      // for multiplication we only use 2 tile patterns
      // sequence below represents the row and col direction to choose
      // relative to tile selected
      this.patterns = [
        [[1,0]],  // right
        [[0,1]],  // down
        [[-1,0]],  // left
        [[0, -1]]  // up
      ];
    }

    this.sprite = Images.addImage(0, 0, 'playing_area', this).setOrigin(0);
    this.calculateGridPositions();
  };

  calculateGridPositions () {

    // /log("total cols = " + this.totalCols);

    const width = this.getBounds().width;
    const height = this.getBounds().height - 20; // border at bottom

    const tile = new NumberTile(this.scene, 0);
    const tileSize = tile.getBounds().width;

    const spaceX = (width - (this.totalCols * tileSize)) / (this.totalCols + 1);
    const spaceY = (height - (this.totalRows * tileSize)) / (this.totalRows + 1);

    let curX = 0;
    let curY = this.y + spaceY + tileSize / 2;

    for (let y = 0; y < this.totalRows; y++) {

      curX = this.x + spaceX + tileSize / 2;

      for (let x = 0; x < this.totalCols; x++) {

        this.positions.push({ x: curX, y: curY });
        curX += spaceX + tileSize;

      }

      curY += spaceY + tileSize;
    }
  }

  setupInitialBoard (isDemo) {

    // clear some arrays
    while (this.tilesSelected.length > 0) {
      this.tilesSelected.pop();
    }

    while (this.tiles.length > 0) {
      const tile = this.tiles.pop();
      if (tile !== null) {
        this.remove(tile);
      }
    }

    while (this.emptySpaces.length > 0) {
      this.emptySpaces.pop();
    }
    if (gameData.gameType === ADDITION) {
      switch (this.currentLevel) {
        case 1:
          this.maxTileValue = 4;
          break;

        case 2:
          this.maxTileValue = 6;
          break;

        case 3:
          this.maxTileValue = 9;
          break;
      }
    }
    else if (gameData.gameType === MULTIPLICATION) {
      switch (this.currentLevel) {
        case 1:
          this.maxTileValue = 6;
          break;

        case 2:
          this.maxTileValue = 8;
          break;

        case 3:
          this.maxTileValue = 12;
          break;
      }
    }

    for (let i = 0; i < (this.totalCols * this.totalRows); i++) {

      let tileValue = null;

      if (isDemo || gameData.gameType === ADDITION) {
        tileValue = (i % this.maxTileValue) + 1;
      }
      else {
        tileValue = Phaser.Math.Between(1, this.maxTileValue);
      }

      const tile = new NumberTile(this.scene, tileValue, isDemo);
      tile.name = `tile_${i + 1}`;
      tile.on(NumberTile.TILE_SELECTED, () => {this.tileClicked(tile.number, tile);});

      // play tile add sound

      this.putTileAtPositionByIndex(i,tile);
    }

    this.animateTileSetup();

    this.resetLevel();

    this.scene.time.addEvent({ delay: 2000, callback: () => {this.blinkRandomStar();} });
  }

  tileClicked(value, tile) {

    // a tile has been clicked -- if it it to stay highlighted
    // and show connections with other tiles -- we need to determine if the existing
    // selected tiles are neighbors

    // if this is first tile selected, it automatically gets placed in queue

    const tileClicked = tile;

    if (this.tilesSelected.length === 0) {

      Audio.playOneShot('select_1', this.scene);

      this.tilesSelected.push(tileClicked);

      this.emit(NumberTile.TILE_SELECTED, value);

    }
    else {

      // run through list of selected tiles to find a neighbor

      let foundOneNeighbor = false;
      let clickedOnAlreadySelectedTile = false;

      for (let i = 0; i < this.tilesSelected.length; i++) {

        const nTile = this.tilesSelected[i];

        const direction = this.checkIsNeighbor(tileClicked.row, tileClicked.column, nTile.row, nTile.column);

        if (direction === 'same') {
          clickedOnAlreadySelectedTile = true;
          break;
        }

        if (direction !== false) {

          foundOneNeighbor = true;

          tileClicked.showLink(direction);

        }
      }

      // a tile has been that is not neighbor (or is identical) -- remove all selected tiles
      if (!foundOneNeighbor || clickedOnAlreadySelectedTile) {

        Audio.playOneShot('deselect', this.scene);

        tileClicked.deselect();

        while (this.tilesSelected.length > 0) {
          const nTile = this.tilesSelected.pop();
          nTile.deselect();
        }

        this.emit(PlayingGrid.TILES_ERASED, 0);
      }
      else {

        // we find a neighbor, add to array of selected tiles

        this.tilesSelected.push(tileClicked);

        let tileSelectedCount = this.tilesSelected.length;
        if (tileSelectedCount > 5) tileSelectedCount = 5;

        Audio.playOneShot(`select_${tileSelectedCount}`, this.scene);

        this.emit(NumberTile.TILE_SELECTED, value);
      }

    }

  }

  blinkRandomStar () {

    if (this.starIsShowing || this.starsShown > 2) {
      return;
    }

    this.starsShown++;

    const validIndices = [];

    for (let i = 0; i < this.tiles.length; i++) {

      const tile = this.tiles[i];

      if (tile !== null) {
        validIndices.push(i);
      }
    }

    const randomIndex = Phaser.Math.Between(0, validIndices.length - 1);

    const tileIndex = validIndices[randomIndex];

    const tile = this.tiles[tileIndex];

    tile.addStar();
    tile.on(NumberTile.STAR_REMOVED, () => {this.starRemovedFromTile(tile);});

    this.starIsShowing = true;

  }

  checkIsNeighbor(r1, c1, r2, c2) {

    const rDiff = Math.abs(r1 - r2);
    const cDiff = Math.abs(c1 - c2);

    if (rDiff === 0 && cDiff === 0) {
      return 'same';
    }

    if ((rDiff === 1 && cDiff === 0) || (rDiff === 0 && cDiff === 1)) {

      // we have a neighbor tile. return value for whether it is
      // neighbor "north", "south" , "east" or "west"
      // the reference tile will be first. we will determine whether the row and
      // and column of second values is "north", "south" etc. relative to first

      const diffRows = r1 - r2;
      const diffCols = c1 - c2;

      if (rDiff === 1) { // its a north or south difference

        if (diffRows > 0) {
          return 'north';
        }
        else {
          return 'south';
        }

      }
      else { // its east or west

        if (diffCols > 0) {
          return 'east';
        }
        else {
          return 'west';
        }

      }

    }

    return false;

  }

  putTileAtPositionByIndex (index, tile) {

    this.tiles[index] = tile;

    const posXY = this.positions[index];

    tile.x = posXY.x;
    tile.y = posXY.y;

    tile.alpha = 0;
    tile.enabled = false;

    this.add(tile);

    // finally add row and col to tile itself

    const rc = this.indexToRowCol(index);

    const row = rc[0];
    const col = rc[1];

    tile.row = row;
    tile.column = col;

  }

  indexToRowCol (index) {

    let row = Math.floor(index / this.totalCols);
    let col = index % this.totalCols;
    row++;
    col++;

    // log(" set row = " + row + ", set col = " + col);

    return [row, col];
  }

  animateTileSetup () {

    // create sequence of row,cols -- we'll animate appearance
    // based on this sequence

    const seq = [[3,3],
      [4, 3],
      [4,2],
      [3, 2],
      [2,2],
      [2,3],
      [2,4],
      [3,4],
      [4,4],
      [5, 4],
      [5,3],
      [5, 2],
      [5, 1],
      [4,1],
      [3,1],
      [2,1],
      [1,1],
      [1,2],
      [1,3],
      [1,4],
      [1,5],
      [2,5],
      [3,5],
      [4,5],
      [5,5]];

    let cumulativeTime = 0;
    for (let i = 0; i < seq.length; i++) {

      const pos = seq[i];
      const r = pos[0];
      const c = pos[1];

      const tile = this.getTileAtPosition(r, c);

      cumulativeTime += 100;
      this.scene.time.addEvent({ delay: cumulativeTime, callback: () => {tile.appear();} });
    }

    this.scene.time.addEvent({ delay: 100 * seq.length, callback: () => {this.emit(PlayingGrid.GRID_CREATED);} });
  }

  getTileAtPosition (row, col) {

    const index = this.rowColToIndex(row, col);

    const tile = this.tiles[index];

    return tile;

  }

  rowColToIndex (row, col) {

    row--;
    col--;

    const index = (row * this.totalCols) + col;

    return index;
  }

  resetLevel () {

    if (this.yellowStar1) this.remove(this.yellowStar1, true);
    if (this.yellowStar2) this.remove(this.yellowStar2, true);
    if (this.yellowStar3) this.remove(this.yellowStar3, true);

    this.yellowStar1 = null;
    this.yellowStar2 = null;
    this.yellowStar3 = null;

    this.starsCollected = 0;
    this.starIsShowing = false;
    this.starsShown = 0;
  }

  starRemovedFromTile (tile) {

    tile.starIsShowing = false;
    this.starIsShowing = false;
  }

  addStarAtIndex (tileIndex) {

    if (tileIndex === undefined) tileIndex = 2;

    const tile = this.tiles[tileIndex];

    if (tile) {
      tile.addStar();
      tile.on('STAR_REMOVED', () => {this.starRemovedFromTile(tile);});

      this.starIsShowing = true;
    }

  }

  clickTile (row, col) {

    const tile = this.getTileAtPosition(row, col);

    tile.simulateClick();
  }

  targetHit () {

    // the target number has been reached by the sum of the tile values
    // steps to perform
    // (1) make selected tiles disappear
    // (2) slide all tiles in vertical plane up and down to fill in blanks
    // (3) slide all tiles in horizontal plane left and right to fill in blanks

    Audio.playOneShot('clear', this.scene);

    let cumulativeTime = 0;
    let tilesValue = gameData.gameType === ADDITION ? 0 : 1;
    for (let i = 0; i < this.tilesSelected.length; i++) {

      const tile = this.tilesSelected[i];

      // keep track of empty spaces by row && col
      this.emptySpaces.push([tile.row, tile.column]);
      cumulativeTime += 100;

      this.scene.time.addEvent({ delay: cumulativeTime, callback: () => {tile.disappear();} });

      // remove item from tiles[] array because we will be sliding other tiles
      // into this position

      const index = this.rowColToIndex(tile.row, tile.column);
      this.tiles[index] = null;

      if (gameData.gameType === ADDITION) tilesValue += tile.number;
      else if (gameData.gameType === MULTIPLICATION)tilesValue *= tile.number;
    }

    this.emit(PlayingGrid.TARGET_HIT, tilesValue);

    // waitDoFunction(cumulativeTime, this.emptySelectedTiles.bind(this));
    this.scene.time.addEvent({ delay: cumulativeTime, callback: this.emptySelectedTiles, callbackScope: this });
    this.scene.time.addEvent({ delay: cumulativeTime + 50, callback: this.moveTilesToEmptySpaces, callbackScope: this });
  }

  moveTilesToEmptySpaces () {

    // first check for movement south, then north, then east, then west
    // all tiles strive to be in the center point row = 3, col =3
    // if there is an adjacent empty space not preventing a tile from moving
    // it can move 1 or 2 spaces (in grid 5 x 5) in order to move close to center
    // tile should never cross center point (r=3, c=3)

    this.sortTilesSouth();
    this.sortTilesSouth();

    this.sortTilesNorth();
    this.sortTilesNorth();

    this.moveVerticalTiles();

    this.sortTilesWest();
    this.sortTilesWest();

    this.sortTilesEast();
    this.sortTilesEast();

    // waitDoFunction(500, this.moveHorizontalTiles.bind(this));
    this.scene.time.addEvent({ delay: 500, callback: () => {this.moveHorizontalTiles();} });

  }

  sortTilesSouth () {

    // check SOUTH movement (down) 2nd row -- only top 2 rows will move down and need to be checked
    // start from center and move up
    for (let row = 2; row > 0; row--) {

      for (let col = 5; col > 0; col--) {

        const tile = this.getTileAtPosition(row, col);
        if (tile === null) {

          continue;
        }

        let moved = 0;

        for (let i = 0; i < this.emptySpaces.length; i++) {

          const pos = this.emptySpaces[i];
          const emptyRow = pos[0];
          const emptyCol = pos[1];
          const direction = this.checkIsNeighbor(tile.row, tile.column, emptyRow, emptyCol);

          if (direction === 'south' && moved === 0) {
            moved++;

            this.scheduledVerticalMoves[tile.name] = [tile, emptyRow, emptyCol];

            // move empty position to above

            this.emptySpaces[i][0] = tile.row;
            this.emptySpaces[i][1] = tile.column;

            this.putTileAtPosition(tile, emptyRow, emptyCol);
          }
        }
      }
    }
  }

  putTileAtPosition (tile, row, col) {
    // null out old position in array
    const oldIndex = this.rowColToIndex(tile.row, tile.column);
    this.tiles[oldIndex] = null;
    const index = this.rowColToIndex(row, col);

    // assume this.tiles[index] is empty
    this.tiles[index] = tile;
    tile.row = row;
    tile.column = col;
  }

  sortTilesNorth () {

    // check NORTH moves
    for (let row = 4; row < 6; row++) {

      for (let col = 5; col > 0; col--) {

        const tile = this.getTileAtPosition(row, col);
        if (tile === null) {

          continue;
        }

        let moved = 0;
        for (let i = 0; i < this.emptySpaces.length; i++) {

          const pos = this.emptySpaces[i];
          const emptyRow = pos[0];
          const emptyCol = pos[1];

          const direction = this.checkIsNeighbor(tile.row, tile.column, emptyRow, emptyCol);

          if (direction === 'north' && moved === 0) {

            moved++;

            this.scheduledVerticalMoves[tile.name] = [tile, emptyRow, emptyCol];
            this.emptySpaces[i][0] = tile.row;
            this.emptySpaces[i][1] = tile.column;

            this.putTileAtPosition(tile, emptyRow, emptyCol);
          }
        }
      }
    }
  }

  moveVerticalTiles () {
    const vKeys = Object.keys(this.scheduledVerticalMoves);

    if (vKeys.length > 0) {
      Audio.playOneShot('woosh', this.scene);
    }

    for (let i = 0; i < vKeys.length; i++) {

      const tileKey = vKeys[i];
      const array = this.scheduledVerticalMoves[tileKey];
      const tile = array[0];
      const row = array[1];
      const col = array[2];

      this.moveTileToItsRowCol(tile);
    }

    while (this.scheduledVerticalMoves.length > 0) {
      this.scheduledVerticalMoves.pop();
    }
  }

  moveTileToItsRowCol (tile) {

    const index = this.rowColToIndex(tile.row, tile.column);

    const posXY = this.positions[index];

    const x = posXY.x;
    const y = posXY.y;

    if (tile.scene) Tweens.addTween(tile, 400, { x, y }, Phaser.Math.Easing.Bounce.Out);
  }

  sortTilesEast () {

    // check EAST moves

    for (let col = 4; col < 6; col++) {

      for (let row = 5; row > 0; row--) {

        const tile = this.getTileAtPosition(row, col);
        if (tile === null) {

          continue;
        }

        let moved = 0;
        for (let i = 0; i < this.emptySpaces.length; i++) {

          const pos = this.emptySpaces[i];
          const emptyRow = pos[0];
          const emptyCol = pos[1];

          const direction = this.checkIsNeighbor(tile.row, tile.column, emptyRow, emptyCol);

          if (direction === 'east' && moved === 0) {
            moved++;

            this.scheduledHorizontalMoves[tile.name] = [tile, emptyRow, emptyCol];

            this.emptySpaces[i][0] = tile.row;
            this.emptySpaces[i][1] = tile.column;

            this.putTileAtPosition(tile, emptyRow, emptyCol);
          }
        }
      }
    }

    // we've moved all the tiles, now show a star if we've used it
    this.blinkRandomStar();
  }

  moveHorizontalTiles() {

    const hKeys = Object.keys(this.scheduledHorizontalMoves);

    if (hKeys.length > 0) {
      Audio.playOneShot('woosh', this.scene);
    }

    for (let i = 0; i < hKeys.length; i++) {

      const tileKey = hKeys[i];
      const array = this.scheduledHorizontalMoves[tileKey];
      const tile = array[0];

      const row = array[1];
      const col = array[2];

      this.moveTileToItsRowCol(tile);
    }

    while (this.scheduledHorizontalMoves.length > 0) {
      this.scheduledHorizontalMoves.pop();

    }

    this.scene.time.addEvent({ delay: 1000, callback: () => {this.tilesReady();} });
  }

  tilesReady () {
    // we are at end of level if we have 0 or 1 tile left

    const count = this.countNonEmptyTiles();

    this.blinkRandomStar();

    if (count === 1 || count === 0) {

      this.emit(PlayingGrid.LEVEL_COMPLETED);
    }
  }

  emptySelectedTiles() {

    // clear selected tiles
    while (this.tilesSelected.length > 0) {
      const tile = this.tilesSelected.pop();

      if (tile.hasStar) {

        this.animateStarToScore(tile);

        this.emit(PlayingGrid.STAR_COLLECTED);

        this.starIsShowing = false;
      }
    }
  }

  animateStarToScore(tile) {

    Audio.playOneShot('star', this.scene);


    this.star = Images.addImage(tile.x - 25, tile.y - 105, 'white-star', this);

    let x = 588;
    switch (this.starsCollected) {
      case 0:
        x = 588 + 10;
        break;

      case 1:
        x = 609 + 10;
        break;

      case 2:
        x = 629 + 10;
        break;
    }

    Tweens.addTween(this.star, 1000, { x, y: -110 }, Phaser.Math.Easing.Quadratic.Out, () => {
      Tweens.addTween(this.star, 10, { scale: 1.2 }, Phaser.Math.Easing.Quadratic.Out, () => {
        Tweens.addTween(this.star, 1000, { angle: 360 * 6 }, Phaser.Math.Easing.Quadratic.In, () => {
          Tweens.addTween(this.star, 200, { scale: 0.5 }, Phaser.Math.Easing.Quadratic.Out, () => {
            this.addYellowStar();
          });
        });
      });
    });
  }

  addYellowStar () {

    this.remove(this.star, true);

    let x = 588;

    const yellowStar = Images.addImage(0, 0, 'star', this);

    switch (this.starsCollected) {
      case 0:
        x = 588 + 10;
        this.yellowStar1 = yellowStar;
        break;

      case 1:
        x = 609 + 10;
        this.yellowStar2 = yellowStar;
        break;

      case 2:
        x = 629 + 10;
        this.yellowStar3 = yellowStar;
        break;

    }


    yellowStar.x = x;
    yellowStar.y = -107;
    if (gameData.gameType === MULTIPLICATION) yellowStar.y -= 5;
    yellowStar.scaleX = 1.2;
    yellowStar.scaleY = 1.2;

    this.starsCollected++;
  }

  sortTilesWest () {

    // check WEST moves
    for (let col = 2; col > 0; col--) {

      for (let row = 5; row > 0; row--) {

        const tile = this.getTileAtPosition(row, col);
        if (tile === null) {

          continue;
        }

        let moved = 0;

        for (let i = 0; i < this.emptySpaces.length; i++) {

          const pos = this.emptySpaces[i];
          const emptyRow = pos[0];
          const emptyCol = pos[1];

          const direction = this.checkIsNeighbor(tile.row, tile.column, emptyRow, emptyCol);

          if (direction === 'west' && moved === 0) {

            moved++;

            this.scheduledHorizontalMoves[tile.name] = [tile, emptyRow, emptyCol];

            this.emptySpaces[i][0] = tile.row;
            this.emptySpaces[i][1] = tile.column;

            this.putTileAtPosition(tile, emptyRow, emptyCol);
          }
        }
      }
    }
  }

  countNonEmptyTiles () {

    let count = 0;

    for (let i = 0; i < this.tiles.length; i++) {

      const tile = this.tiles[i];

      if (tile !== null) {
        count++;
      }
    }

    return count;

  }

  findValidTargetNumber () {

    // we will search the grid for a patter of 2 numbers
    // in order to return a valid target number.

    // iterate though the grid and find the first number that is non-empty
    // once we find one, we'll test all patterns to get a target number

    // first let's choose a random tile index to start with
    // this function insures we pick a random VALID index (ie. non-empty)
    const randomIndex = this.chooseRandomTileIndex();

    let tilesLeft = 0;
    let tilesLeftValue = 0;

    for (let i = randomIndex; i < this.tiles.length; i++) {

      const tile = this.tiles[i];

      if (tile != null) {
        tilesLeft++;
        tilesLeftValue += tile.number;

        const startCol = tile.column;
        const startRow = tile.row;

        // columns and row valid number are between 1 - 5
        // if we produce a row or col outside of these values
        // we reject the pattern.  if we have a valid pattern, but
        // it produces a null tile (empty space) we reject it

        // test the patterns to see if we get a valid number

        for (let a = 0; a < this.patterns.length; a++) {
          if (gameData.gameType === ADDITION) {

            const pattern = this.patterns[a];
            let tile2 = pattern[0];
            let tile3 = pattern[1];

            const tile2Row = startRow + tile2[0];
            const tile2Col = startCol + tile2[1];

            const tile3Row = tile2Row + tile3[0];
            const tile3Col = tile2Col + tile3[1];

            if (tile2Row < 1 || tile2Row > 5) continue;

            if (tile2Col < 1 || tile2Col > 5) continue;

            if (tile3Row < 1 || tile3Row > 5) continue;

            if (tile3Col < 1 || tile3Col > 5) continue;


            tile2 = this.getTileAtPosition(tile2Row, tile2Col);
            if (tile2 == null) continue;

            tile3 = this.getTileAtPosition(tile3Row, tile3Col);
            if (tile3 == null) continue;

            // unless there's only 2 tiles, 1 of these patterns
            // should have gotten us to here
            // so we add up the 3 tiles and that is our target number

            const targetNumber = tile.number + tile2.number + tile3.number;
            return targetNumber;
          }
          else if (gameData.gameType === MULTIPLICATION) {
            const pattern = this.patterns[a];
            let tile2 = pattern[0];

            const tile2Row = startRow + tile2[0];
            const tile2Col = startCol + tile2[1];

            if (tile2Row < 1 || tile2Row > 5) continue;
            if (tile2Col < 1 || tile2Col > 5) continue;

            tile2 = this.getTileAtPosition(tile2Row, tile2Col);

            if (tile2 == null) continue;

            // unless there's only 2 tiles, 1 of these patterns
            // should have gotten us to here
            // so we add up the 3 tiles and that is our target number

            const targetNumber = tile.number * tile2.number;

            return targetNumber;
          }
        }
      }
    }

    // here now continue tile search with second half of this.tiles[] array
    // from 0 to randomIndex

    for (let i = 0; i < randomIndex; i++) {

      const tile = this.tiles[i];

      if (tile != null) {
        tilesLeft++;
        tilesLeftValue += tile.number;

        const startRow = tile.row;
        const startCol = tile.column;

        // columns and row valid number are between 1 - 5
        // if we produce a row or col outside of these values
        // we reject the pattern.  if we have a valid pattern, but
        // it produces a null tile (empty space) we reject it

        // test the patterns to see if we get a valid number

        if (gameData.gameType === ADDITION) {
          for (let a = 0; a < this.patterns.length; a++) {
            const pattern = this.patterns[a];
            let tile2 = pattern[0];
            let tile3 = pattern[1];

            const tile2Row = startRow + tile2[0];
            const tile2Col = startCol + tile2[1];

            const tile3Row = tile2Row + tile3[0];
            const tile3Col = tile2Col + tile3[1];

            if (tile2Row < 1 || tile2Row > 5) continue;

            if (tile2Col < 1 || tile2Col > 5) continue;

            if (tile3Row < 1 || tile3Row > 5) continue;

            if (tile3Col < 1 || tile3Col > 5) continue;

            tile2 = this.getTileAtPosition(tile2Row, tile2Col);
            if (tile2 == null) continue;

            tile3 = this.getTileAtPosition(tile3Row, tile3Col);
            if (tile3 == null) continue;

            // unless there's only 2 tiles, 1 of these patterns
            // should have gotten us to here
            // so we add up the 3 tiles and that is our target number

            const targetNumber = tile.number + tile2.number + tile3.number;
            return targetNumber;
          }

        }
        else if (gameData.gameType === MULTIPLICATION) {

          for (let a = 0; a < this.patterns.length; a++) {

            const pattern = this.patterns[a];
            let tile2 = pattern[0];

            const tile2Row = startRow + tile2[0];
            const tile2Col = startCol + tile2[1];

            if (tile2Row < 1 || tile2Row > 5) continue;
            if (tile2Col < 1 || tile2Col > 5) continue;


            tile2 = this.getTileAtPosition(tile2Row, tile2Col);

            if (tile2 == null) continue;

            // unless there's only 2 tiles, 1 of these patterns
            // should have gotten us to here
            // so we add up the 3 tiles and that is our target number

            const targetNumber = tile.number * tile2.number;

            return targetNumber;
          }
        }
      }
    }

    if (tilesLeft === 2 && gameData.gameType === ADDITION) {

      return tilesLeftValue;

    }

    // if we got we are done with level
    return -1;
  }

  chooseRandomTileIndex () {

    const validIndices = [];

    for (let i = 0; i < this.tiles.length; i++) {

      const tile = this.tiles[i];


      if (tile != null) {
        validIndices.push(i);
      }
    }

    const randomIndex = Phaser.Math.Between(0, validIndices.length - 1);

    const tileIndex = validIndices[randomIndex];

    return tileIndex;
  }
}